import React from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Upload, X } from 'lucide-react';

import {
  fiscalStatusColor,
  fiscalStatusLabel,
  formatDate,
  formatMoney,
  supplierLabel,
} from './selloutUtils';

const invoiceLabel = (invoice) => {
  if (!invoice) return '';
  const series = invoice.series ? `${invoice.series}-` : '';
  const total = invoice.total !== undefined && invoice.total !== null ? ` | ${formatMoney(invoice.total)}` : '';
  return `${series}${invoice.number}${total}`;
};

export default function SellOutCreditNoteForm({
  form,
  onChange,
  suppliers,
  selectedSupplier,
  invoices,
  selectedInvoice,
  loadingSuppliers,
  loadingInvoices,
  disabled,
}) {
  const setValue = (key, value) => onChange({ [key]: value });

  return (
    <Stack spacing={2}>
      <Alert severity="info">
        La nota de credito se registra separada y se vincula a una factura promocional Sell Out.
      </Alert>

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
        <Autocomplete
          fullWidth
          options={suppliers}
          value={selectedSupplier}
          loading={loadingSuppliers}
          disabled={disabled}
          onChange={(_, supplier) => onChange({
            supplier_id: supplier?.id || null,
            linked_invoice_id: null,
          })}
          getOptionLabel={supplierLabel}
          isOptionEqualToValue={(option, value) => option?.id === value?.id}
          renderInput={(params) => <TextField {...params} size="small" label="Proveedor" />}
        />
        <Autocomplete
          fullWidth
          options={invoices}
          value={selectedInvoice}
          loading={loadingInvoices}
          disabled={disabled || !form.supplier_id}
          onChange={(_, invoice) => setValue('linked_invoice_id', invoice?.id || null)}
          getOptionLabel={invoiceLabel}
          isOptionEqualToValue={(option, value) => option?.id === value?.id}
          renderInput={(params) => <TextField {...params} size="small" label="Factura promocional a cancelar" />}
        />
      </Stack>

      {selectedInvoice ? (
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
          <Box>
            <Typography variant="caption" color="text.secondary">Estado fiscal de factura</Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              <Chip
                size="small"
                label={fiscalStatusLabel(selectedInvoice.fiscal_status)}
                color={fiscalStatusColor(selectedInvoice.fiscal_status)}
              />
              <Typography variant="body2" color="text.secondary">
                Aplicado {formatMoney(selectedInvoice.credit_notes_applied || 0)} de {formatMoney(selectedInvoice.total || 0)}
              </Typography>
            </Stack>
          </Box>
          <Typography variant="body2" color="text.secondary">
            Fecha factura: {formatDate(selectedInvoice.date) || '-'}
          </Typography>
        </Stack>
      ) : null}

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
        <TextField
          size="small"
          label="Numero"
          value={form.number || ''}
          disabled={disabled}
          onChange={(event) => setValue('number', event.target.value)}
          sx={{ flex: 1 }}
        />
        <TextField
          size="small"
          label="Serie"
          value={form.series || ''}
          disabled={disabled}
          onChange={(event) => setValue('series', event.target.value)}
          sx={{ flex: 1 }}
        />
        <TextField
          size="small"
          type="date"
          label="Fecha"
          value={form.date || ''}
          disabled={disabled}
          onChange={(event) => setValue('date', event.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{ minWidth: { md: 170 } }}
        />
      </Stack>

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
        <TextField
          size="small"
          type="number"
          label="Total nota"
          value={form.total || ''}
          disabled={disabled}
          onChange={(event) => setValue('total', event.target.value)}
          inputProps={{ min: 0, step: '0.01' }}
          sx={{ flex: 1 }}
        />
        <TextField
          size="small"
          type="number"
          label="Monto aplicado"
          value={form.applied_amount || ''}
          disabled={disabled}
          onChange={(event) => setValue('applied_amount', event.target.value)}
          inputProps={{ min: 0, step: '0.01' }}
          sx={{ flex: 1 }}
        />
        <Box sx={{ minWidth: { md: 260 } }}>
          <Button component="label" variant="outlined" startIcon={<Upload size={17} />} disabled={disabled} fullWidth>
            Archivo
            <input
              hidden
              type="file"
              onChange={(event) => setValue('file', event.target.files?.[0] || null)}
            />
          </Button>
          {form.file ? (
            <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 0.75, minWidth: 0 }}>
              <Typography variant="caption" color="text.secondary" noWrap title={form.file.name}>
                {form.file.name}
              </Typography>
              <Button size="small" color="inherit" startIcon={<X size={14} />} onClick={() => setValue('file', null)}>
                Quitar
              </Button>
            </Stack>
          ) : null}
        </Box>
      </Stack>

      <TextField
        size="small"
        multiline
        minRows={2}
        label="Comentario"
        value={form.comment || ''}
        disabled={disabled}
        onChange={(event) => setValue('comment', event.target.value)}
      />
    </Stack>
  );
}
